// Account state for the web editor.
//
// The editor needs a KoliStat account. Accounts are free. The
// kolistat-api Worker signs in a user through Google, Microsoft or a
// magic link and mints an HS256 JWT. The editor stores the token under
// `kolistat.auth.token` and decodes the payload without verifying the
// signature; the Worker is the trust boundary. Every procedure runs for
// a signed-in user, so `getLicenseContext()` reports `licensed` for any
// valid token and `free_only` without one.
//
// Token capture (SEC-011): the Worker's redirect_after target is the
// editor URL with `#token=<jwt>` appended. Fragments are never sent to
// servers in HTTP requests, so the JWT cannot leak through access logs
// or Referer chains. `captureTokenFromUrl()` runs at boot, stores the
// token, and uses `history.replaceState` to scrub the fragment from the
// address bar. Errors stay in `?error=<code>` (informational, not
// secret); `captureAuthErrorFromUrl()` surfaces them to the login UI.
//
// Dev escape hatch: `window.kolistat.setLicense(...)` writes a separate
// `kolistat.dev_license` JSON entry that takes priority only in DEV.
// The whole block tree-shakes away in `bun run build`.
//
// Storage policy: localStorage for v1 (single-origin editor, no
// third-party scripts). A later milestone may switch to httpOnly
// cookies set by the Worker.

import type { LicenseContextWire, LicenseKind } from './wasm-abi';

const AUTH_TOKEN_KEY = 'kolistat.auth.token';
const DEV_LICENSE_KEY = 'kolistat.dev_license';

const FREE_ONLY: LicenseContextWire = { kind: 'free_only' };

// Hardcoded base. The VITE_API_BASE_URL override exists so a tester can
// point a local editor at a preview Worker deployment without editing
// source. Falls through to prod URL on a built artifact.
const API_BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  (import.meta.env.DEV ? 'http://localhost:8787' : 'https://api.kolistat.com');

interface JwtPayload {
  sub?: string;
  email?: string;
  features?: string[];
  exp?: number;
  terms?: string | null;
}

export interface AuthUser {
  email: string;
  termsVersion: string | null;
}

// --- storage primitives --------------------------------------------------
// All wrap try/catch so a quota-exceeded localStorage (private mode,
// disabled cookies) downgrades to "no token" rather than throwing into
// the run path.

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    console.warn(`[kolistat auth] localStorage read failed for ${key}`, e);
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    console.warn(`[kolistat auth] localStorage write failed for ${key}`, e);
  }
}

function removeStorage(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.warn(`[kolistat auth] localStorage remove failed for ${key}`, e);
  }
}

// --- JWT decode ---------------------------------------------------------

// base64url → utf8 string. Pure decode; never verifies signature — see
// module header for the threat model.
function base64UrlDecode(s: string): string | null {
  try {
    let b64 = s.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4;
    if (pad === 2) b64 += '==';
    else if (pad === 3) b64 += '=';
    else if (pad !== 0) return null;
    // atob handles Latin-1; round through escape/unescape for proper UTF-8.
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

function decodeJwtPayload(token: string): JwtPayload | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  const json = base64UrlDecode(parts[1]);
  if (json == null) return null;
  try {
    const obj = JSON.parse(json) as unknown;
    if (typeof obj !== 'object' || obj === null) return null;
    return obj as JwtPayload;
  } catch {
    return null;
  }
}

function isExpired(payload: JwtPayload): boolean {
  if (typeof payload.exp !== 'number') return false; // tokens without exp don't expire client-side
  return Date.now() / 1000 >= payload.exp;
}

// Seconds remaining until `exp`; +Infinity when no exp is set. Negative
// when the token has already expired. Caller decides whether to refresh
// or sign out.
function secondsUntilExp(payload: JwtPayload): number {
  if (typeof payload.exp !== 'number') return Number.POSITIVE_INFINITY;
  return payload.exp - Date.now() / 1000;
}

function isLicenseKind(v: unknown): v is LicenseKind {
  return v === 'free_only' || v === 'trial' || v === 'licensed';
}

// --- public state accessors ---------------------------------------------

/// Read the JWT payload from storage. Returns null if absent, malformed,
/// or expired (and silently clears an expired token so subsequent calls
/// don't re-decode the dead value).
function loadActivePayload(): JwtPayload | null {
  const token = readStorage(AUTH_TOKEN_KEY);
  if (token == null || token === '') return null;
  const payload = decodeJwtPayload(token);
  if (payload == null) {
    removeStorage(AUTH_TOKEN_KEY);
    return null;
  }
  if (isExpired(payload)) {
    removeStorage(AUTH_TOKEN_KEY);
    return null;
  }
  return payload;
}

/** The context the engine runs with. Always returns a usable value and
 *  never throws.
 *
 *  Resolution order (highest priority first):
 *    1. DEV-only `kolistat.dev_license` JSON (escape hatch). It wins
 *       over a JWT in DEV so a tester can try any context without
 *       signing out first. Dead code in a production bundle.
 *    2. A valid, unexpired JWT in `kolistat.auth.token` gives
 *       `{kind: 'licensed'}`. Every procedure runs for a signed-in
 *       user, so the token's claims are not consulted.
 *    3. `{kind: 'free_only'}` otherwise.
 *
 *  This path never produces `{kind: 'trial'}`. The 30-day trial is a
 *  native-build feature that runs without an account. */
export function getLicenseContext(): LicenseContextWire {
  if (import.meta.env.DEV) {
    const raw = readStorage(DEV_LICENSE_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as unknown;
        if (typeof parsed === 'object' && parsed !== null) {
          const obj = parsed as Record<string, unknown>;
          if (isLicenseKind(obj.kind)) {
            const out: LicenseContextWire = { kind: obj.kind };
            if (typeof obj.license_email === 'string') out.license_email = obj.license_email;
            if (typeof obj.trial_days_left === 'number') out.trial_days_left = obj.trial_days_left;
            return out;
          }
        }
      } catch {
        // fall through to JWT / free_only
      }
    }
  }

  if (loadActivePayload()) return { kind: 'licensed' };
  return FREE_ONLY;
}

export function isAuthenticated(): boolean {
  return loadActivePayload() != null;
}

export function getUser(): AuthUser | null {
  const payload = loadActivePayload();
  if (!payload || typeof payload.email !== 'string') return null;
  return {
    email: payload.email,
    termsVersion: typeof payload.terms === 'string' ? payload.terms : null,
  };
}

// --- auth actions -------------------------------------------------------

// The query string rides along so a `?mode=tutorial` link survives the
// sign-in round trip. The Worker only checks the origin of
// `redirect_after`.
function defaultRedirectAfter(): string {
  return window.location.origin + window.location.pathname + window.location.search;
}

/// Kick off Google OAuth. Asks the Worker for a `redirect_url`, then
/// navigates the current page to it; the user returns to
/// `redirect_after?token=<jwt>` after consent. Editor state is not
/// preserved across the redirect — beforeunload guards unsaved buffers
/// elsewhere, and the Worker round-trip is typically <2s.
export async function loginWithGoogle(redirectAfter?: string): Promise<void> {
  return startOAuthRedirect('google', redirectAfter);
}

/// Kick off Microsoft (Entra ID) OAuth. Same shape as Google — Worker
/// returns a redirect URL we navigate to; user returns to
/// `redirect_after?token=<jwt>` after consent. Supports both work/school
/// (corporate Microsoft 365 / Entra) and personal Microsoft accounts via
/// the `common` multi-tenant endpoint configured on the Worker.
export async function loginWithMicrosoft(redirectAfter?: string): Promise<void> {
  return startOAuthRedirect('microsoft', redirectAfter);
}

/// Worker-mediated OAuth bootstrap. POSTs to `/auth/<provider>/start`,
/// reads the consent URL, and navigates the current page to it. Editor
/// state across the redirect is not preserved — the Worker round-trip
/// is typically <2s and the caller (login modal) disables its trigger
/// button while we're in flight.
async function startOAuthRedirect(provider: 'google' | 'microsoft', redirectAfter?: string): Promise<void> {
  const after = redirectAfter ?? defaultRedirectAfter();
  const res = await fetch(`${API_BASE_URL}/auth/${provider}/start`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ redirect_after: after }),
  });
  if (!res.ok) {
    throw new Error(`${provider} login: ${res.status} ${res.statusText}`);
  }
  const body = (await res.json()) as { redirect_url?: unknown };
  if (typeof body.redirect_url !== 'string') {
    throw new Error(`${provider} login: malformed response (no redirect_url)`);
  }
  window.location.href = body.redirect_url;
}

/// Ask the Worker to email a magic link. The endpoint always returns
/// `{sent: true}` even for unknown addresses — that's the Worker's
/// privacy posture, not a client-side check to add.
export async function requestMagicLink(email: string, redirectAfter?: string): Promise<void> {
  const after = redirectAfter ?? defaultRedirectAfter();
  const res = await fetch(`${API_BASE_URL}/auth/magic-link/send`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, redirect_after: after }),
  });
  if (!res.ok) {
    throw new Error(`magic link: ${res.status} ${res.statusText}`);
  }
}

/// Best-effort POST to `/auth/signout` then wipe local state. Failures
/// against the Worker are swallowed — the user-visible truth (this
/// browser is signed out) is fully captured by the localStorage wipe.
/// The server-side revoke is nice-to-have for "all my sessions" hygiene
/// but the user has no UI today that depends on it returning 204.
export async function signOut(): Promise<void> {
  const token = readStorage(AUTH_TOKEN_KEY);
  if (token) {
    try {
      await fetch(`${API_BASE_URL}/auth/signout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        // keepalive lets the request survive a tab-close gesture if the
        // user triggers Sign Out and immediately navigates away. 64KB
        // body limit doesn't matter — body is empty.
        keepalive: true,
      });
    } catch {
      // Network failure — local wipe still authoritative for this tab.
    }
  }
  removeStorage(AUTH_TOKEN_KEY);
  if (refreshTimer != null) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  notifyAuthStateChange();
}

// --- terms of service ---------------------------------------------------

export interface TermsInfo {
  version: string;
  termsUrl: string;
  privacyUrl: string;
}

let termsInfoPromise: Promise<TermsInfo | null> | null = null;

/// The current terms version from `GET /terms`, fetched once per page
/// load. A failure resolves to null, which means "do not prompt", and
/// is not cached, so the next call tries again.
export function fetchTermsInfo(): Promise<TermsInfo | null> {
  if (termsInfoPromise) return termsInfoPromise;
  termsInfoPromise = (async (): Promise<TermsInfo | null> => {
    try {
      const res = await fetch(`${API_BASE_URL}/terms`);
      if (!res.ok) return null;
      const body = (await res.json()) as { version?: unknown; terms_url?: unknown; privacy_url?: unknown };
      if (typeof body.version !== 'string' || typeof body.terms_url !== 'string' || typeof body.privacy_url !== 'string') {
        return null;
      }
      return { version: body.version, termsUrl: body.terms_url, privacyUrl: body.privacy_url };
    } catch {
      return null;
    }
  })();
  void termsInfoPromise.then((info) => {
    if (info === null) termsInfoPromise = null;
  });
  return termsInfoPromise;
}

/// Record acceptance of `version` and swap in the fresh token the Worker
/// returns, so the `terms` claim is current without a re-login.
export async function acceptTerms(version: string): Promise<void> {
  const token = readStorage(AUTH_TOKEN_KEY);
  if (!token) throw new Error('not signed in');
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}/auth/terms/accept`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ version }),
    });
  } catch {
    throw new Error('could not reach the server; try again');
  }
  if (res.status === 401) {
    if (readStorage(AUTH_TOKEN_KEY) !== token) {
      throw new Error('your session was refreshed; press Accept again');
    }
    hardSignOut();
    throw new Error('your session has expired; sign in again');
  }
  if (res.status === 400) {
    const body = (await res.json().catch(() => ({}))) as { current?: unknown };
    const current = typeof body.current === 'string' ? body.current : 'a newer version';
    throw new Error(`the terms changed to ${current}; reload the page to see them`);
  }
  if (res.status === 429) {
    throw new Error('too many attempts; try again in an hour');
  }
  if (!res.ok) throw new Error('could not reach the server; try again');
  const body = (await res.json()) as { token?: unknown };
  if (typeof body.token !== 'string' || body.token === '') throw new Error('accept terms: malformed response');
  writeStorage(AUTH_TOKEN_KEY, body.token);
  scheduleNextRefresh();
  notifyAuthStateChange();
}

/// Inspect the current URL for a `#token=<jwt>` fragment (the landing
/// target for both OAuth and magic-link flows). If present, store the
/// token and scrub the fragment from the address bar so a reload or
/// share doesn't leak the JWT. Returns true if a token was captured.
///
/// SEC-011: tokens live in the URL fragment, not the query string,
/// because fragments are never sent to servers in HTTP requests and
/// don't end up in destination access logs or Referer chains.
export function captureTokenFromUrl(): boolean {
  const hash = window.location.hash;
  if (!hash || hash.length < 2) return false;
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const token = params.get('token');
  if (!token) return false;
  writeStorage(AUTH_TOKEN_KEY, token);
  // Keep query + path intact (any non-auth query like `?mode=tutorial`
  // must survive). Drop the entire fragment — its only role here is
  // the credential we just consumed.
  const clean = window.location.pathname + window.location.search;
  window.history.replaceState({}, '', clean);
  notifyAuthStateChange();
  return true;
}

export interface AuthErrorInfo {
  error: string;
  reason?: string;
}

/// Inspect the current URL for `?error=<code>[&reason=<…>]` and return
/// the parsed pair if present. Scrubs `error` and `reason` from the
/// query (preserving any other params like `?mode=tutorial`) and leaves
/// the fragment intact (typically already empty since
/// `captureTokenFromUrl` runs first and drops the fragment).
///
/// SEC-004 follow-up: surfaces the Worker's
/// `?error=account_exists&reason=different_provider` redirect so the
/// login modal can render a useful message instead of a blank screen.
export function captureAuthErrorFromUrl(): AuthErrorInfo | null {
  const url = new URL(window.location.href);
  const error = url.searchParams.get('error');
  if (!error) return null;
  const reason = url.searchParams.get('reason') ?? undefined;
  url.searchParams.delete('error');
  url.searchParams.delete('reason');
  const clean = url.pathname + (url.search ? url.search : '') + url.hash;
  window.history.replaceState({}, '', clean);
  return reason !== undefined ? { error, reason } : { error };
}

// --- refresh scheduler --------------------------------------------------
// Goal: a tab kept open across the 24h access-token TTL never sees a
// re-auth prompt. We schedule a setTimeout REFRESH_LEAD_SECONDS before
// `exp`; on focus, we also catch up if the timer was throttled while
// the tab was backgrounded (Chrome/Firefox throttle setTimeout to ≥1min
// for hidden tabs).

const REFRESH_LEAD_SECONDS = 5 * 60;
const RETRY_AFTER_SECONDS = 60;

let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let refreshInFlight: Promise<boolean> | null = null;
// Single-shot retry guard so a transient 5xx doesn't loop forever.
// Reset on any successful refresh.
let retryAttempted = false;

type AuthStateListener = () => void;
let authStateListeners: AuthStateListener[] = [];

/// Subscribe to auth-state changes (token swap, sign-in, sign-out).
/// Returns an unsubscribe handle. Listeners fire after the storage
/// mutation completes, so they can read the new state synchronously.
export function onAuthStateChange(fn: AuthStateListener): () => void {
  authStateListeners.push(fn);
  return () => {
    authStateListeners = authStateListeners.filter((l) => l !== fn);
  };
}

function notifyAuthStateChange(): void {
  for (const fn of authStateListeners) {
    try {
      fn();
    } catch (e) {
      console.warn('[kolistat auth] listener threw', e);
    }
  }
}

function clearRefreshTimer(): void {
  if (refreshTimer != null) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

// Hard sign-out path used by the refresh loop when the server says the
// session is dead (401) or when a network retry has exhausted itself.
// Skips the /auth/signout POST — the session is already gone on the
// server, so an extra POST would just bounce on 401.
function hardSignOut(): void {
  removeStorage(AUTH_TOKEN_KEY);
  clearRefreshTimer();
  notifyAuthStateChange();
}

function scheduleNextRefresh(): void {
  clearRefreshTimer();
  const payload = loadActivePayload();
  if (!payload) return;
  const secs = secondsUntilExp(payload);
  if (!Number.isFinite(secs)) return; // no exp claim → no scheduling
  // refresh REFRESH_LEAD_SECONDS before exp; clamp to >=0 so a token
  // already inside the lead window kicks an immediate refresh.
  const delaySecs = Math.max(0, secs - REFRESH_LEAD_SECONDS);
  refreshTimer = setTimeout(() => {
    void performRefresh();
  }, delaySecs * 1000);
}

async function performRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async (): Promise<boolean> => {
    const token = readStorage(AUTH_TOKEN_KEY);
    if (!token) {
      hardSignOut();
      return false;
    }
    const payload = decodeJwtPayload(token);
    // If the token is already past exp, refresh will 401 — short-circuit
    // straight to sign-out so we don't burn a round-trip.
    if (payload && isExpired(payload)) {
      hardSignOut();
      return false;
    }
    let res: Response;
    try {
      res = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
    } catch {
      // Network failure: one-shot retry, then treat as hard sign-out.
      if (!retryAttempted) {
        retryAttempted = true;
        clearRefreshTimer();
        refreshTimer = setTimeout(() => {
          void performRefresh();
        }, RETRY_AFTER_SECONDS * 1000);
        return false;
      }
      retryAttempted = false;
      hardSignOut();
      return false;
    }
    if (res.status === 401) {
      retryAttempted = false;
      hardSignOut();
      return false;
    }
    if (!res.ok) {
      // 5xx / other transient: same one-shot retry posture as network.
      if (!retryAttempted) {
        retryAttempted = true;
        clearRefreshTimer();
        refreshTimer = setTimeout(() => {
          void performRefresh();
        }, RETRY_AFTER_SECONDS * 1000);
        return false;
      }
      retryAttempted = false;
      hardSignOut();
      return false;
    }
    let body: { token?: unknown };
    try {
      body = (await res.json()) as { token?: unknown };
    } catch {
      hardSignOut();
      return false;
    }
    if (typeof body.token !== 'string' || body.token === '') {
      hardSignOut();
      return false;
    }
    writeStorage(AUTH_TOKEN_KEY, body.token);
    retryAttempted = false;
    scheduleNextRefresh();
    notifyAuthStateChange();
    return true;
  })().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

function onVisibilityChange(): void {
  if (document.visibilityState !== 'visible') return;
  const payload = loadActivePayload();
  if (!payload) return;
  const secs = secondsUntilExp(payload);
  if (!Number.isFinite(secs)) return;
  // Inside the lead window or past exp → refresh now. Otherwise the
  // existing timer is still good.
  if (secs <= REFRESH_LEAD_SECONDS) {
    void performRefresh();
  }
}

// Another tab wrote or removed the token. This happens when a tab that
// shows "check your email" waits while the magic link signs in from the
// tab the email client opened. `key` is null when the whole storage
// was cleared, which removes the token as well.
function onStorage(ev: StorageEvent): void {
  if (ev.key !== AUTH_TOKEN_KEY && ev.key !== null) return;
  scheduleNextRefresh();
  notifyAuthStateChange();
}

/// Install the refresh lifecycle. Called once at boot from main.ts
/// AFTER `captureTokenFromUrl()` so a fresh post-redirect token is
/// what the first schedule reads.
export function startAuthLifecycle(): void {
  scheduleNextRefresh();
  document.addEventListener('visibilitychange', onVisibilityChange);
  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('storage', onStorage);
  }
}

// --- DEV console affordance ---------------------------------------------
// `window.kolistat.setLicense({kind:'licensed', ...})` from devtools
// bypasses the JWT path and writes to `kolistat.dev_license`. Gated by
// `import.meta.env.DEV` so the assignment + helpers tree-shake out of
// production. Renamed from B4.2's dev-console global for brand
// consistency.

interface KolistatDevApi {
  setLicense(ctx: LicenseContextWire): void;
  clearLicense(): void;
  current(): LicenseContextWire;
  /// Force a `/auth/refresh` round-trip. Resolves to true on success,
  /// false if the session was hard-killed. Used by the manual browser
  /// test harness for B4.3e — see B4.3e-2 brief.
  refresh(): Promise<boolean>;
}

declare global {
  interface Window {
    kolistat?: KolistatDevApi;
  }
}

if (import.meta.env.DEV) {
  const setDevLicense = (ctx: LicenseContextWire): void => {
    writeStorage(DEV_LICENSE_KEY, JSON.stringify(ctx));
    notifyAuthStateChange();
  };
  const clearDevLicense = (): void => {
    removeStorage(DEV_LICENSE_KEY);
    notifyAuthStateChange();
  };
  window.kolistat = {
    setLicense: (ctx) => {
      console.warn('[kolistat] dev escape hatch — bypasses JWT auth; writes kolistat.dev_license');
      setDevLicense(ctx);
    },
    clearLicense: clearDevLicense,
    current: getLicenseContext,
    refresh: () => performRefresh(),
  };
}
